import { endOfDay, endOfWeek, format, startOfDay, startOfWeek } from 'date-fns';
import moment from 'moment';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { CalendarWeek } from '../../../../components/ui';
import { WCS } from '../../../../components/ui/calendar/calendar-week/week-utils';
import {
  ICalendarEvent,
  IMenuRenderInfo,
  IRenderInfo,
  WeekCalendarHandle,
} from '../../../../components/ui/calendar/calendars';
import { showNotification } from '../../../../helpers/notification-helper';
import { getShortDateFormat } from '../../../../helpers/utils-helpers';
import useOperationStatus from '../../../../lib/hooks/useOperationStatus';
import { actions as eVisitActions } from '../../../../store/ducks/e-visits';
import { isRangeError } from '../../../../type-guards/e-visits';
import { TDay, TExistingEVisitRule, TRulesManipulationError } from '../../../../types/e-visits';
import TStore from '../../../../types/store';
import { TUserMedicalInstitution } from '../../../../types/users';
import { generateRulesAddArray } from '../../utils';
import { CalendarEditSC } from './calendar-edit.styled';
import AddForm from './components/add-form';
import { IFormValues } from './components/add-form/add-form.schema';
import { isRepeatingEvent, isRepeatingRule } from './components/add-form/add-form.util';
import ChangeTypeModal from './components/change-type-modal';
import DeleteTypeModal from './components/delete-type-modal';
import EditItem from './components/edit-item';
import EmptyItem from './components/empty-item';
import ExistingItem from './components/existing-item';

const WEEK_STARTS_ON = Number(process.env.REACT_APP_WEEK_STARTS_ON) as TDay;

interface CalendarEditProps {
  selectedInstitution: TUserMedicalInstitution | undefined;
  selectedDate: Date;
  setSelectedDate: (d: Date) => void;
}

const CalendarEdit: React.FunctionComponent<CalendarEditProps> = ({
  selectedInstitution,
  selectedDate,
  setSelectedDate,
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const cRef = React.useRef<WeekCalendarHandle<any>>(null);

  // const [selectedDate, setSelectedDate] = React.useState(() => new Date());
  const [editing, setEditing] = React.useState(false);
  const [submittedData, setSubmittedData] = React.useState<IFormValues | undefined>();
  const [eventToDelete, setEventToDelete] = React.useState<TExistingEVisitRule | undefined>(
    undefined,
  );
  const [deleteRepeatingEvent, setDeleteRepeatingEvent] = React.useState(false);

  const currentRules = useSelector((state: TStore) => state.eVisits.eVisitsRules);
  const fetchStatus = useSelector((state: TStore) => state.eVisits.getEVisitRuleStatus);

  const fetch = React.useCallback(() => {
    if (selectedInstitution) {
      dispatch(
        eVisitActions.getEVisitsRulesRequest({
          expertiseCode: selectedInstitution.expertise!.code!,
          medicalInstitutionId: selectedInstitution.id,
          fromDate: startOfWeek(selectedDate, { weekStartsOn: WEEK_STARTS_ON }).toISOString(),
          toDate: endOfWeek(selectedDate, { weekStartsOn: WEEK_STARTS_ON }).toISOString(),
        }),
      );
    }
  }, [dispatch, selectedInstitution, selectedDate]);

  const updateOrCerateEvent = React.useCallback(
    (type: 'all' | 'current', data?: IFormValues) => {
      const finalData = data || submittedData;

      if (!finalData) {
        return undefined;
      }

      if (finalData.id) {
        dispatch(
          eVisitActions.updateEVisitRuleRequest({
            ...finalData,
            id: finalData.id!,
            ...(type === 'current'
              ? { forDate: finalData.fromDate, fromDate: undefined, toDate: undefined }
              : {
                  fromDate: finalData.fromDate,
                  toDate: finalData?.repeat
                    ? finalData.toDate
                    : endOfDay(new Date(finalData.fromDate)).toISOString(),
                }),
            tagId: finalData.selectedTag ? finalData.selectedTag.value : undefined,
          }),
        );
      } else {
        dispatch(
          eVisitActions.createEVisitRuleRequest({
            ...finalData,
            ...(finalData?.repeat
              ? {
                  fromDate: finalData.fromDate,
                  toDate: finalData.toDate,
                }
              : {
                  fromDate: startOfDay(new Date(finalData.fromDate)).toISOString(),
                  toDate: endOfDay(new Date(finalData.fromDate)).toISOString(),
                }),
            tagId: finalData.selectedTag ? finalData.selectedTag.value : undefined,
          }),
        );
      }
      setSubmittedData(undefined);
    },
    [dispatch, submittedData],
  );

  const handleSubmit = React.useCallback(
    (values: IFormValues, originalEvent: ICalendarEvent<TExistingEVisitRule>) => {
      if (
        values.id &&
        isRepeatingEvent(values.fromDate, values.toDate) &&
        isRepeatingRule(originalEvent)
      ) {
        setSubmittedData(values);
      } else if (values) {
        updateOrCerateEvent('all', values);
      }
    },
    [updateOrCerateEvent],
  );

  const deleteEvent = React.useCallback(
    (type?: 'all' | 'current', event?: TExistingEVisitRule) => {
      const finalEvent = event || eventToDelete;

      if (!finalEvent) {
        return undefined;
      }

      const date = startOfDay(new Date(finalEvent.fromTime)).toISOString();

      dispatch(
        eVisitActions.deleteEVisitRuleRequest({
          id: finalEvent.id,
          ...(type === 'all' ? { fromDate: date } : { forDate: date }),
        }),
      );
      setEventToDelete(undefined);
      setDeleteRepeatingEvent(false);
    },
    [dispatch, eventToDelete],
  );

  const handleDelete = React.useCallback(
    (event: ICalendarEvent<TExistingEVisitRule>) => {
      setDeleteRepeatingEvent(isRepeatingRule(event));
      setEventToDelete(event.eventData!);
    },
    [deleteEvent],
  );

  const _renderMenu = React.useCallback(
    ({ event, direction, onClose, type, onEdit }: IMenuRenderInfo<TExistingEVisitRule>) => {
      return (
        <AddForm
          event={event}
          onClose={onClose}
          horizontalPosition={direction?.horizontalPosition}
          onAdd={onEdit}
          onSubmit={handleSubmit}
          onDelete={handleDelete}
        />
      );
    },
    [handleSubmit, handleDelete],
  );

  const _renderEvent = React.useCallback(({ item, onEdit }: IRenderInfo<TExistingEVisitRule>) => {
    if (!item.eventData?.id) {
      return <EmptyItem onClick={onEdit} item={item} />;
    }
    return <ExistingItem item={item} onClick={onEdit} />;
  }, []);

  const _renderAddEvent = React.useCallback((event: ICalendarEvent<TExistingEVisitRule>) => {
    return <EditItem item={event} />;
  }, []);

  const _onMenuOpen = React.useCallback(
    (event: ICalendarEvent<TExistingEVisitRule>) => {
      if (editing) {
        return false;
      }

      setEditing(true);

      return false;
    },
    [editing],
  );

  const onSuccess = React.useCallback(() => {
    cRef.current?.closeMenu(true);
    showNotification({
      type: 'SUCCESS',
      title: formatMessage({ id: 'eVisit_rules_success' }),
      copy: '',
    });
    fetch();
  }, [cRef, fetch, formatMessage]);

  const onError = React.useCallback(
    (error: TRulesManipulationError) => {
      if (isRangeError(error)) {
        console.log('err', error);
        showNotification({
          type: 'ERROR',
          title: formatMessage({ id: `eVisit_rules_error_${error.errCode}` }),
          copy: formatMessage(
            { id: 'eVisit_rules_error_range_error' },
            {
              windows: error.detail.conflicts
                .map((d) => moment(d.fromDate).format(getShortDateFormat()))
                .join(', '),
            },
          ),
        });
      } else {
        console.log('err', error);
        showNotification({
          type: 'ERROR',
          title: formatMessage({ id: `eVisit_rules_error_${error.errCode}` }),
          copy: '',
        });
      }
    },
    [cRef, formatMessage],
  );

  const _onMenuClose = React.useCallback(() => {
    setEditing(false);
    return false;
  }, []);

  React.useEffect(() => {
    fetch();
  }, [fetch]);

  useOperationStatus({
    stateGetter: (state) => state.eVisits.createEVisitRuleStatus,
    errorGetter: (state) => state.eVisits.creatEVisitRuleError,
    resetAction: eVisitActions.createEVisitRuleReset,
    onSuccess,
    onError,
  });

  useOperationStatus({
    stateGetter: (state) => state.eVisits.updateEVisitRuleStatus,
    errorGetter: (state) => state.eVisits.updateEVisitRuleError,
    resetAction: eVisitActions.updateEVisitRuleReset,
    onSuccess,
    onError,
  });

  useOperationStatus({
    stateGetter: (state) => state.eVisits.deleteEVisitRuleStatus,
    errorGetter: (state) => state.eVisits.deleteEVisitRuleError,
    resetAction: eVisitActions.deleteEVisitRuleReset,
    onSuccess,
    onError,
  });

  const mappedEvents = React.useMemo(() => {
    const obj = Object.values(
      generateRulesAddArray(currentRules || {}, {
        type: 'only-future',
        medicalInstitutionId: selectedInstitution?.id,
        expertiseCode: selectedInstitution?.expertise?.code,
        weekStart: startOfWeek(selectedDate, {
          weekStartsOn: WEEK_STARTS_ON,
        }),
      }),
    ).flat();
    return obj;
  }, [currentRules, selectedInstitution, selectedDate]);

  return (
    <>
      <CalendarEditSC>
        <CalendarWeek
          ref={cRef}
          events={mappedEvents}
          renderEvent={_renderEvent}
          renderMenu={_renderMenu}
          displayAllDay
          selectedDate={format(selectedDate, 'yyyy-MM-dd')}
          onChange={setSelectedDate}
          onMenuOpen={_onMenuOpen}
          hoursPadding={1}
          weekStartsOn={WEEK_STARTS_ON}
          renderAddEvent={_renderAddEvent}
          onMenuClose={_onMenuClose}
          menuWidth={WCS.menuWidth}
          loading={fetchStatus === 'pending'}
          mode={'edit'}
        />
      </CalendarEditSC>
      <ChangeTypeModal
        visible={submittedData !== undefined}
        setVisible={setSubmittedData as any}
        onSuccess={updateOrCerateEvent}
        event={submittedData}
      />
      <DeleteTypeModal
        visible={eventToDelete !== undefined}
        setVisible={setEventToDelete as any}
        onSuccess={deleteEvent}
        event={eventToDelete}
        deleteRepeatingEvent={deleteRepeatingEvent}
      />
    </>
  );
};

export default React.memo(CalendarEdit);
